import type { ColumnDef } from '@tanstack/react-table';

import type { ReportColumn } from '@/lib/api';
import { LOCALE } from '@/lib/locale';

/**
 * Build `<DataTable>` columns from the metadata the API sends — CLAUDE.md §4.3, §5.7.
 *
 * **The structure is given, not inferred**: the server names each column's `group` and this
 * arranges the flat list into the nested shape TanStack renders as two header rows. Columns keep
 * their declared order, and a group is a run of adjacent columns sharing a heading — the registry
 * refuses non-contiguous groups, because a header span cannot be drawn across a gap.
 */
/**
 * `row` is the whole record the cell sits in — a formatter that compares one column against
 * another (a year against the year before it) cannot do its job from the value alone. Most
 * formatters ignore it.
 */
export type Formatter = (
  value: unknown,
  column: ReportColumn,
  row?: Record<string, unknown>,
) => React.ReactNode;

/**
 * Whole figures for factory-scale measures. `maximumFractionDigits: 0` is required — the locale
 * default allows three, so a SQL `ROUND(…, 2)` value would render "9,012.50" rather than "9,013".
 */
export const integer = new Intl.NumberFormat(LOCALE, {
  maximumFractionDigits: 0,
});
/** Two-decimal figures — percentages, SMV, unit prices. */
export const decimal = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Default rendering by declared type; a report overrides per column where it must. Decimals belong
 * to percentages and nothing else — a `number` measure is factory-scale, where ".00" is noise.
 */
export const defaultFormat: Formatter = (value, column) => {
  if (value === null || value === undefined || value === '') {
    // An empty cell, not a zero: in a pivot most cells are absent, and 0 would claim a real nothing.
    return <span className="text-muted-foreground/40">—</span>;
  }
  switch (column.type) {
    case 'integer':
    case 'number':
      // Round before format so 12.6 becomes "13", not "12.6" if a caller swaps the formatter.
      return integer.format(Math.round(Number(value)));
    case 'percent':
      return `${decimal.format(Number(value))}%`;
    default:
      return String(value);
  }
};

/** The numeric types, which right-align so figures line up digit under digit. */
const NUMERIC = new Set(['number', 'integer', 'percent']);

/**
 * One column of the layout, as the export endpoint takes it (§5.9). Not data — the server resolves
 * every field against the view's declaration and re-runs the query itself.
 */
export interface ExportColumn {
  field: string;
  label: string;
  group?: string;
}

/**
 * Read the export layout **off the rendered columns**, so the file and the screen cannot drift.
 * A leaf needs `meta.exportLabel` and a group `meta.exportGroup`; a column without one is skipped
 * rather than exported under its field name, which keeps control columns out of the file.
 */
export function exportColumns<TRow>(defs: readonly ColumnDef<TRow>[]): ExportColumn[] {
  const out: ExportColumn[] = [];

  const walk = (columns: readonly ColumnDef<TRow>[], group: string | undefined): void => {
    for (const column of columns) {
      const meta = column.meta;
      const children = 'columns' in column ? column.columns : undefined;
      if (children !== undefined && children.length > 0) {
        walk(children, meta?.exportGroup ?? group);
        continue;
      }
      const field = column.id;
      const label = meta?.exportLabel;
      if (field === undefined || label === undefined) {
        continue;
      }
      out.push(group === undefined ? { field, label } : { field, label, group });
    }
  };

  walk(defs, undefined);
  return out;
}

interface Options {
  /** Columns to leave out — guards and other metadata the reader has no use for. */
  hide?: (column: ReportColumn) => boolean;
  format?: Formatter;
  /**
   * Offer a sort control on every leaf, not only the fields the server whitelists. The order is the
   * browser's either way; what this widens is the offer, so a leaf sorted here that the `sort:`
   * whitelist (§5.4) does not carry orders the screen but 400s the export.
   */
  clientSortable?: boolean;
}

export function groupedColumns<TRow extends Record<string, unknown>>(
  columns: ReportColumn[],
  options: Options = {},
): ColumnDef<TRow>[] {
  const { hide, format = defaultFormat, clientSortable = false } = options;
  const visible = hide === undefined ? columns : columns.filter((c) => !hide(c));

  const leaf = (column: ReportColumn): ColumnDef<TRow> => ({
    // The report.yaml field name — it travels back as SortSpec.field, so it cannot be prettified.
    id: column.field,
    accessorKey: column.field,
    header: column.label,
    // The server's whitelist decides which headers are offered, and it arrives with the column
    // (§5.4); defaulting to false keeps the offer governed and the export able to repeat it.
    enableSorting: clientSortable || column.sortable === true,
    // Prefer content-sized columns over equal stretch (paired with table `w-max`).
    size: NUMERIC.has(column.type) ? 72 : 96,
    minSize: 48,
    meta: {
      // What the export writes as this column's heading — see `exportColumns`.
      exportLabel: column.label,
      align: NUMERIC.has(column.type) ? 'right' : 'left',
      // Only straight-summable measures join the totals row; `percent` is excluded because its
      // true total is a ratio of two other totals, which §4.3 keeps in SQL.
      ...(column.type === 'number' || column.type === 'integer'
        ? { total: 'sum' as const }
        : {}),
      // Tighter numeric cells; identity strings keep a little room for the header filter.
      cellClassName: NUMERIC.has(column.type) ? 'px-1 font-mono tabular-nums' : undefined,
      headerClassName: NUMERIC.has(column.type) ? 'px-1' : undefined,
    },
    cell: ({ row }) => format(row.original[column.field], column, row.original),
  });

  const out: ColumnDef<TRow>[] = [];
  let run: ReportColumn[] = [];
  let heading: string | null = null;

  const flush = () => {
    if (run.length === 0) return;
    if (heading === null) {
      out.push(...run.map(leaf));
    } else {
      out.push({
        id: `group:${heading}`,
        header: heading,
        // A heading is not a field: `group:Sewing` names no column, so nothing can order by it.
        enableSorting: false,
        meta: { exportGroup: heading },
        columns: run.map(leaf),
      });
    }
    run = [];
  };

  for (const column of visible) {
    const group = column.group ?? null;
    if (group !== heading) {
      flush();
      heading = group;
    }
    run.push(column);
  }
  flush();
  return out;
}
